import { useEffect } from 'react';
import { processMaterializedTable } from '@/storedTableEditor/storedTableMaterialization.utilities';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type {
  MaterializedTableOutput,
  ProcessedMaterializedTable,
  StoredTableSearchFailure,
} from '@/tableDefinitionEditor/tableDefinition.types';
import type { StoredTableColumnOutputV1 } from '@seeqdev/api/model/StoredTableColumnOutputV1';
import type { StoredTableOutputV1 } from '@seeqdev/api/model/StoredTableOutputV1';

export interface StoredTableState {
  /** ID of the stored table currently open in the editor, or `undefined` if none. */
  tableId: string | undefined;
  /** The stored table item as returned by the API. */
  storedTable: StoredTableOutputV1 | undefined;
  /** Column definitions for the currently open stored table. */
  columns: StoredTableColumnOutputV1[];
  /** True while the stored table item/materialized data is being fetched. */
  isLoadingStoredTable: boolean;
  /** Display name of the currently open stored table. */
  name: string | undefined;
  /** Row/column data for the currently open stored table, processed for grid display. */
  materializedTable: ProcessedMaterializedTable | undefined;
  /** Per-row search failures surfaced by the last materialization request. */
  searchFailures: StoredTableSearchFailure[];
  /** Whether the search-failure detail modal is open. */
  showSearchFailuresModal: boolean;
  /** Messages shown in the currently open search-failure detail modal. */
  searchFailureMessages: string[];
}

export interface StoredTableActions {
  setTableId: (tableId: string | undefined) => void;
  /** Sets the stored table and derives `name`/`columns` from it, matching the API response shape. */
  setStoredTable: (storedTable: StoredTableOutputV1) => void;
  setIsLoadingStoredTable: (isLoadingStoredTable: boolean) => void;
  setMaterializedTable: (materializedTable: MaterializedTableOutput | undefined) => void;
  setStoredTableName: (name: string | undefined) => void;
  setSearchFailures: (searchFailures: StoredTableSearchFailure[]) => void;
  setShowSearchFailuresModal: (showSearchFailuresModal: boolean) => void;
  /** Opens the search-failure modal with the given messages. */
  setShowSearchFailureMessages: (messages: string[]) => void;
  /** Resets the store to its initial state, optionally overriding specific fields. */
  reset: (overrides?: Partial<StoredTableState>) => void;
}

export type StoredTableDehydratedState = Pick<
  StoredTableState,
  'tableId' | 'isLoadingStoredTable' | 'searchFailures' | 'showSearchFailuresModal' | 'searchFailureMessages'
>;

const initialState: StoredTableState = Object.freeze({
  tableId: undefined,
  storedTable: undefined,
  columns: [],
  isLoadingStoredTable: false,
  name: undefined,
  materializedTable: undefined,
  searchFailures: [],
  showSearchFailuresModal: false,
  searchFailureMessages: [],
});

const storedTableActionsFactory: ActionsFactory<StoredTableState, StoredTableActions> = (set, get, resetState) => ({
  setTableId: (tableId) =>
    set((draft) => {
      draft.tableId = tableId;
    }),

  setStoredTable: (storedTable) =>
    set((draft) => {
      draft.storedTable = storedTable;
      draft.name = storedTable.name;
      draft.columns = storedTable.columns;
    }),

  setIsLoadingStoredTable: (isLoadingStoredTable) =>
    set((draft) => {
      draft.isLoadingStoredTable = isLoadingStoredTable;
    }),

  setMaterializedTable: (materializedTable) =>
    set((draft) => {
      draft.materializedTable = processMaterializedTable(materializedTable, get().columns);
    }),

  setStoredTableName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setSearchFailures: (searchFailures) =>
    set((draft) => {
      draft.searchFailures = searchFailures;
    }),

  setShowSearchFailuresModal: (showSearchFailuresModal) =>
    set((draft) => {
      draft.showSearchFailuresModal = showSearchFailuresModal;
    }),

  setShowSearchFailureMessages: (messages) =>
    set((draft) => {
      draft.showSearchFailuresModal = true;
      draft.searchFailureMessages = messages;
    }),

  reset: resetState,
});

const STORE_NAME = 'StoredTable';

const [StoredTableStoreProvider, useStoredTableStoreInstance, useStoredTableSelector] = createStore(
  STORE_NAME,
  initialState,
  storedTableActionsFactory,
);

export { StoredTableStoreProvider };

export const dehydrateStoredTableState = (state: StoredTableState): StoredTableDehydratedState => ({
  tableId: state.tableId,
  isLoadingStoredTable: state.isLoadingStoredTable,
  searchFailures: state.searchFailures,
  showSearchFailuresModal: state.showSearchFailuresModal,
  searchFailureMessages: state.searchFailureMessages,
});

export const useStoredTableTableId = () => useStoredTableSelector((state) => state.tableId);
export const useStoredTableStoredTable = () => useStoredTableSelector((state) => state.storedTable);
export const useStoredTableColumns = () => useStoredTableSelector((state) => state.columns);
export const useStoredTableIsLoadingStoredTable = () => useStoredTableSelector((state) => state.isLoadingStoredTable);
export const useStoredTableName = () => useStoredTableSelector((state) => state.name);
export const useStoredTableMaterializedTable = () => useStoredTableSelector((state) => state.materializedTable);
export const useStoredTableSearchFailures = () => useStoredTableSelector((state) => state.searchFailures);
export const useStoredTableShowSearchFailuresModal = () =>
  useStoredTableSelector((state) => state.showSearchFailuresModal);
export const useStoredTableSearchFailureMessages = () => useStoredTableSelector((state) => state.searchFailureMessages);

export const useStoredTableActions = () => useStoredTableStoreInstance().actions;

/**
 * Legacy singleton bridge for non-React consumers (e.g. `storedTable.utilities.ts`,
 * `storedTable.agGrid.utilities.ts`, `stateSynchronizer.service.ts`) that cannot use hooks.
 */
export function legacyCreateStoredTableStore() {
  return createStoreInstance<StoredTableState, StoredTableActions>(initialState, storedTableActionsFactory, {
    storeName: STORE_NAME,
  });
}

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<StoredTableStoreProvider>` with UI-driven assertions instead.
 */
export function createTestStoredTableStore(overrides?: Partial<StoredTableState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestStoredTableStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, storedTableActionsFactory);
}

export const usePersistStoredTableStore = () => {
  const register = useRegisterPersistedStore();
  const store = useStoredTableStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqStoredTableStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateStoredTableState,
    });
  }, [register, store]);
};
